import { ApiError } from '@/lib/api'
import type {
  JoinCustomFieldValue,
  JoinFormErrors,
  JoinOptionsResponse,
  PersonFieldName,
  PersonForm,
  OrganizationForm,
  PublicJoinCustomField,
  PublicMembershipLevel,
  WizardStep,
} from '@/pages/PublicJoinFormPage.types'

export const personFieldTypes: Record<PersonFieldName, string> = {
  name: 'text',
  email: 'email',
  phone: 'tel',
}

export function customFieldsForLevel(
  options: JoinOptionsResponse,
  level: { kind?: PublicMembershipLevel['kind'] } | null,
) {
  const appliesTo = level?.kind === 'organization' ? 'organizations' : 'people'
  return options.custom_fields.filter(
    (field) => !field.applies_to || field.applies_to === appliesTo || field.applies_to === 'both',
  )
}

export function validateApplication(
  options: JoinOptionsResponse | undefined,
  selectedLevelId: number | null,
  person: PersonForm,
  customFieldValues: Record<string, JoinCustomFieldValue>,
  organization?: OrganizationForm,
) {
  const nextErrors = emptyErrors()
  if (!options) {
    nextErrors.form = 'Join form options could not be loaded.'
    return nextErrors
  }

  if (!selectedLevelId) nextErrors.membershipLevel = 'Choose a Membership Level.'

  if (
    options.membership_levels.find((level) => level.id === selectedLevelId)?.kind === 'organization'
  ) {
    if (!organization?.name.trim())
      nextErrors.organization = { name: 'Organization name is required.' }
  }

  options.identity_fields.forEach((field) => {
    if (field.required && person[field.name].trim() === '') {
      nextErrors.person[field.name] = `${field.label} is required.`
    }
  })
  customFieldsForLevel(
    options,
    options.membership_levels.find((level) => level.id === selectedLevelId) ?? null,
  ).forEach((field) => {
    if (!field.required) return
    const value = customFieldValues[String(field.id)]
    if (customFieldValueBlank(value)) {
      nextErrors.customFields[String(field.id)] = `${field.label} is required.`
    }
  })

  return nextErrors
}

export function reviewApplication({
  options,
  selectedLevelId,
  person,
  customFieldValues,
  organization,
  setErrors,
  setStep,
}: {
  options: JoinOptionsResponse | undefined
  selectedLevelId: number | null
  person: PersonForm
  customFieldValues: Record<string, JoinCustomFieldValue>
  organization: OrganizationForm
  setErrors: (errors: JoinFormErrors) => void
  setStep: (step: WizardStep) => void
}) {
  const nextErrors = validateApplication(
    options,
    selectedLevelId,
    person,
    customFieldValues,
    organization,
  )
  setErrors(nextErrors)
  if (!hasErrors(nextErrors)) setStep('review')
}

export function hasErrors(errors: JoinFormErrors) {
  return Boolean(
    errors.membershipLevel ||
    errors.form ||
    Object.values(errors.organization).some(Boolean) ||
    Object.values(errors.person).some(Boolean) ||
    Object.values(errors.customFields).some(Boolean) ||
    errors.chapters,
  )
}

export function emptyErrors(): JoinFormErrors {
  return { person: {}, organization: {}, customFields: {} }
}

export function apiErrors(error: Error, customFields: PublicJoinCustomField[]) {
  const nextErrors = emptyErrors()
  if (!(error instanceof ApiError) || !hasApiFieldErrors(error.body)) {
    nextErrors.form = error.message
    return nextErrors
  }

  Object.entries(error.body.errors).forEach(([field, messages]) => {
    const message = messages.join(' ')
    if (field === 'membership_level') {
      nextErrors.membershipLevel = message
      return
    }
    if (field === 'chapters') {
      nextErrors.chapters = message
      return
    }
    if (field.startsWith('organization_')) {
      const organizationField = field.replace('organization_', '')
      if (isPersonFieldName(organizationField)) nextErrors.organization[organizationField] = message
      return
    }
    if (isPersonFieldName(field)) {
      nextErrors.person[field] = message
      return
    }

    const customField = customFields.find((candidate) => candidate.label === field)
    if (customField) {
      nextErrors.customFields[String(customField.id)] = message
      return
    }

    nextErrors.form = [nextErrors.form, message].filter(Boolean).join(' ')
  })

  return nextErrors
}

export function serializeCustomFieldValues(
  customFields: PublicJoinCustomField[],
  values: Record<string, JoinCustomFieldValue>,
) {
  return Object.fromEntries(
    customFields.map((field) => {
      const key = String(field.id)
      return [key, customFieldPayloadValue(field, values[key])]
    }),
  )
}

export function displayCustomFieldValue(value: JoinCustomFieldValue | undefined) {
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'None'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return value && value.length > 0 ? value : 'None'
}

export function autoCompleteForPersonField(field: PersonFieldName) {
  if (field === 'name') return 'name'
  if (field === 'email') return 'email'
  return 'tel'
}

function customFieldValueBlank(value: JoinCustomFieldValue | undefined) {
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'boolean') return false
  return value === undefined || value.trim() === ''
}

function customFieldPayloadValue(
  field: PublicJoinCustomField,
  value: JoinCustomFieldValue | undefined,
) {
  if (field.field_type === 'checkbox') return value === true
  if (field.field_type === 'multi-select') return Array.isArray(value) ? value : []
  return typeof value === 'string' ? value : ''
}

function hasApiFieldErrors(body: unknown): body is { errors: Record<string, string[]> } {
  if (typeof body !== 'object' || body === null || !('errors' in body)) return false

  const errors = (body as { errors: unknown }).errors
  return typeof errors === 'object' && errors !== null
}

function isPersonFieldName(field: string): field is PersonFieldName {
  return field === 'name' || field === 'email' || field === 'phone'
}
